import { NotificationTemplateKey } from "@config/notification-templates.config";
import { opportunityReportRepo, userRepo } from "@repositories";
import { BackendOnly } from "@shared/backend/backend-only.decorator";
import { HyperBackendMethod } from "@shared/backend/hyper-backend-method";
import {
  REQUEST_DELETED_REASON,
  REQUEST_DELETED_REASON_MASSAGE,
  WEEK_DAYS,
  WEME_APP_BASE_URL,
  WEME_SUPPORT_WHATSAPP_CTA,
} from "@shared/helpers/constants";
import { areOpportunitySurveysDisabled } from "@shared/helpers/opportunity-surveys";
import { pickGroupChildBaseFields } from "@shared/helpers/opportunity-group-child-fields";
import {
  buildGroupChildBulkUpdateFields,
  pickParentBulkUpdate,
  selectGroupChildBulkRelations,
  selectGroupChildBulkUpdateWrite,
  type GroupChildBulkRelations,
  type GroupChildBulkUpdatePatchInput,
  validateGroupChildBulkUpdatePatch,
} from "@shared/helpers/group-instance-bulk-update";
import {
  buildExistingSlotKeys,
  GROUP_INSTANCE_BULK_MAX,
  groupInstanceIdentityKey,
} from "@shared/helpers/group-instance-schedule";
import {
  computeParentSyncFromChildren,
  type OpportunityGroupChildSnapshot,
} from "@shared/helpers/opportunity-group";
import { opportunitySeatsAreFull } from "@shared/helpers/opportunity-occupancy";
import {
  ADMIN_GROUP_ARCHIVED_SUCCESS,
  ADMIN_GROUP_RESTORED_SUCCESS,
  ADMIN_GROUP_CREATE_INSTANCE_ERROR,
  ADMIN_GROUP_CREATE_INSTANCE_SUCCESS,
  adminCannotArchiveWithParticipantsMessage,
} from "@shared/helpers/opportunity-group-admin-copy";
import { hasValidCoordinates, syncOpportunityCoordinatesGeog } from "@shared/helpers/geo";
import { textBuilder } from "@shared/helpers/text-builder";
import { sendMessageToRecipients } from "@shared/notifications/notifications-facade";
import { sendTemplatedUserNotification } from "@shared/notifications/templated-user-notification";
import {
  buildFeedPath,
  buildOpportunityPath,
  NOTIFICATION_ACTION_SURVEY,
} from "@shared/notifications/notification-navigation";
import { Roles } from "@shared/Roles";
import dayjs from "dayjs";
import { Allow, EntityFilter, remult, repo } from "remult";
import { Category } from "../category";
import { Chat } from "../chat";
import { ChatEntry } from "../chat-entry";
import { ChatMember } from "../chat-member";
import { DateAndTimeRange } from "../date-and-time-range";
import { GroupOpportunity } from "../group-opportunity";
import { Mission } from "../mission";
import { OpportunityAvailability } from "../opportunity-availability";
import { OpportunityCategory } from "../opportunity-category";
import { OpportunityInteractionLog } from "../opportunity-interaction-log";
import { OpportunityParticipant } from "../opportunity-participant";
import { OpportunityPopulation } from "../opportunity-population";
import { OpportunityReport } from "../opportunity-report";
import { OpportunitySkill } from "../opportunity-skill";
import { Population } from "../population";
import { SubCategory } from "../sub-category";
import { RepeatedOpportunity } from "../repeated-opportunity";
import { Team } from "../team";
import { TeamOpportunity } from "../team-opportunity";
import { User } from "../user";
import { Opportunity, type RequestType } from "./";
import { applyRequestApprovedAtForSave } from "./request-approved-at";
import { GooglePlacesController } from "../../controllers/Google-places.controller";

function resolveAssociationRequestedId(
  associationRequested?: { id?: string } | null,
  associationRequestedId?: string | null,
): string | null {
  const id = associationRequested?.id || associationRequestedId || "";
  return id || null;
}

type CategorySelection =
  | string
  | {
      id?: string;
      categoryId?: string;
      opportunityId?: string;
    };

/** Category id from a catalog row, a join row, or a raw string. Join-row compound ids are ignored. */
function readCategoryId(value: CategorySelection | null | undefined): string | undefined {
  if (typeof value === "string") {
    const id = value.trim();
    return id || undefined;
  }
  if (!value || typeof value !== "object") return undefined;
  if (typeof value.categoryId === "string" && value.categoryId.trim()) {
    return value.categoryId.trim();
  }
  if (value.opportunityId != null) return undefined;
  if (typeof value.id === "string" && value.id.trim()) {
    return value.id.trim();
  }
  return undefined;
}

/** Keep a subcategory id only when that row still exists. Empty or missing rows become null. */
async function resolveSubCategoryIdForSave(
  subCategoryId: string | null | undefined,
): Promise<string | null | undefined> {
  if (subCategoryId == null) return subCategoryId;
  const id = subCategoryId.trim();
  if (!id) return null;
  const row = await repo(SubCategory).findId(id);
  return row ? id : null;
}

type GroupChildSlotInput = Pick<
  Opportunity,
  | "startDate"
  | "endDate"
  | "hasTime"
  | "hasStartTime"
  | "timeDescription"
  | "location"
  | "coordinates"
  | "amount"
  | "day"
  | "isRemote"
>;

type GroupParentWithRelations = Opportunity & {
  categories?: Array<{ category?: { id: string } } | { id: string }>;
  populations?: Array<{ population?: { id: string } } | { id: string }>;
  teams?: Array<{ team?: Team }>;
  requiredSkills?: OpportunitySkill[];
};

export interface RequestResponse {
  /**
   * Indicates whether the operation was successful.
   */
  success: boolean;

  /**
   * A message providing additional information about the operation result.
   */
  message: string;

  /**
   * Optional machine-readable code (e.g. CONFLICT).
   */
  code?: string;

  /**
   * The opportunity object associated with the request, if applicable.
   */
  requestOpportunity?: Opportunity;
}

/** APPROVED and PENDING block archive. Declined rows do not. */
async function assignedVolunteerCounts(opportunityIds: string[]): Promise<{
  approved: number;
  pending: number;
}> {
  if (opportunityIds.length === 0) return { approved: 0, pending: 0 };
  const opportunityId =
    opportunityIds.length === 1 ? opportunityIds[0] : { $in: opportunityIds };
  const [approved, pending] = await Promise.all([
    repo(OpportunityParticipant).count({
      opportunityId,
      status: "APPROVED",
    }),
    repo(OpportunityParticipant).count({
      opportunityId,
      status: "PENDING",
    }),
  ]);
  return { approved, pending };
}

function throwIfAssignedVolunteers(
  counts: { approved: number; pending: number },
  isGroupParent = false,
) {
  if (counts.approved === 0 && counts.pending === 0) return;
  throw new Error(
    adminCannotArchiveWithParticipantsMessage(
      counts.approved,
      counts.pending,
      isGroupParent,
    ),
  );
}

export class OpportunityMethods {
  @HyperBackendMethod({
    apiPrefix: "opportunity-methods",
    allowed: true,
    overrideSecurity: true,
  })
  static async getOrganizationId(params: { opportunityId: string }) {
    const opportunity = await repo(Opportunity).findId(params.opportunityId, {
      include: {
        organization: true,
      },
    });
    return opportunity?.organization?.id;
  }

  @HyperBackendMethod({
    apiPrefix: "opportunity-methods",
    allowed: Allow.authenticated,
    overrideSecurity: true,
  })
  static async getCountForRange(config: {
    where: EntityFilter<Opportunity>;
    start: Date;
    length: number;
  }) {
    const { length, start, where } = config;
    const now = dayjs(start).startOf("day");
    const data = await Promise.all(
      Array.from({ length }, async (_, index) => {
        const day = now.add(index, "days");
        return {
          day: day.toDate(),
          count: await remult.repo(Opportunity).count({
            $and: [
              where,
              {
                startDate: { $gte: day.startOf("day").toDate() },
                endDate: { $lte: day.endOf("day").toDate() },
              },
            ],
          }),
        };
      }),
    );
    return data;
  }


  /**
   * Saves a new opportunity request or updates an existing repeated opportunity request.
   * If the request is marked as repeated, it either creates or updates the repeated request.
   * Otherwise, it creates a single opportunity and associates it with the selected teams.
   *
   * @param request - The opportunity request data.
   * @returns A promise resolving to a `RequestResponse` indicating success or failure.
   */
  @HyperBackendMethod({
    apiPrefix: "opportunity-methods",
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async saveRequest(
    request: RequestType,
    status?: string,
  ): Promise<RequestResponse> {
    const {
      selectedTeams = [],
      selectedCategories = [],
      selectedPopulations = [],
      categories = [],
      populations = [],
      editRepeatedType,
      action,
      manager,
      partnerOrganization,
      ...requestData
    } = request;

    const managerId = manager?.id ?? requestData.managerId ?? "";

    const isEditRepeatedRequest = !!editRepeatedType;
    if (
      request.isRepeated &&
      request.repeatConfig?.repeatType !== "NO_REPEAT"
    ) {
      if (isEditRepeatedRequest) {
        return Opportunity.updateRepeatedRequest(request);
      } else {
        if (status === "AID_REQUEST" && request.id) {
          await repo(Opportunity).update(request.id, {
            isDeleted: true,
            deletedAt: new Date(),
          });
        }
        return Opportunity.createRepeatedRequest(request);
      }
    } else {
      const {
        id,
        associationRequested: reqAssociationRequested,
        ...restData
      } = requestData;

      const associationRequestedId = resolveAssociationRequestedId(
        reqAssociationRequested,
        requestData.associationRequestedId,
      );
      const subCategoryId = await resolveSubCategoryIdForSave(
        requestData.subCategoryId,
      );

      // For new requests, always set id to undefined to avoid duplicate key errors
      // If id is provided and this is not a duplicate action, it should be an update
      // which should use applyRequestChanges instead of saveRequest
      const oppToSave = repo(Opportunity).create({
        ...restData,
        subCategoryId,
        associationRequestedId,
        managerId,
        id: undefined, // Always create new opportunity without id to avoid duplicate key errors
        isClosed: action === "duplicate" ? false : requestData.isClosed,
        isFull: action === "duplicate" ? false : requestData.isFull,
        ...(requestData.isOpportunityGroupParent
          ? {
              isOpportunityGroupParent: true,
              isHidden: false,
              parentOpportunityId: null,
            }
          : {}),
      });

      applyRequestApprovedAtForSave(oppToSave, { isNew: true });

      const newOpportunity = await repo(Opportunity).save(oppToSave);

      if (!newOpportunity)
        return { success: false, message: "קרתה תקלה בעת יצירת הבקשה" };

      const saveTeamsResponse = await Opportunity.saveTeamsForOpportunity(
        newOpportunity.id,
        selectedTeams,
      );

      if (!saveTeamsResponse.success)
        throw new Error(saveTeamsResponse.message);

      // Save categories if provided
      // Priority: selectedCategories > categories (from relation)
      const categoriesToSave =
        selectedCategories.length > 0
          ? selectedCategories
          : categories.map((oc) => (oc as any).category || oc).filter(Boolean);

      if (categoriesToSave.length > 0) {
        const saveCategoriesResponse =
          await Opportunity.saveCategoriesForOpportunity(
            newOpportunity.id,
            categoriesToSave,
          );

        if (!saveCategoriesResponse.success)
          throw new Error(saveCategoriesResponse.message);
      }

      // Save populations if provided
      // Priority: selectedPopulations > populations (from relation)
      const populationsToSave =
        selectedPopulations.length > 0
          ? selectedPopulations
          : populations
              .map((op) => (op as any).population || op)
              .filter(Boolean);

      if (populationsToSave.length > 0) {
        const savePopulationsResponse =
          await Opportunity.savePopulationsForOpportunity(
            newOpportunity.id,
            populationsToSave,
          );

        if (!savePopulationsResponse.success)
          throw new Error(savePopulationsResponse.message);
      }

      return {
        success: true,
        message: "המשימה נוצרה בהצלחה",
        requestOpportunity: newOpportunity,
      };
    }
  }

  /**
   * Updates an existing opportunity request.
   * Currently not implemented.
   *
   * @param request - Partial data for the opportunity to be updated.
   * @returns A promise resolving to a `RequestResponse` indicating success or failure.
   */
  @HyperBackendMethod({
    apiPrefix: "opportunity-methods",
    allowed: Allow.authenticated,
    overrideSecurity: true,
  })
  static async applyRequestChanges({
    selectedTeams = [],
    selectedCategories = [],
    selectedPopulations = [],
    categories = [],
    repeatConfig,
    action,
    editRepeatedType,
    image,
    requestImage,
    subCategory,
    organization,
    repeatedOpportunity,
    participantUsers,
    groups,
    requesterAvailability,
    reports,
    populations = [],
    form,
    teams,
    requiredSkills,
    associationRequested,
    partnerOrganization,
    manager,
    progressStatus,
    ...requestData
  }: RequestType): Promise<RequestResponse> {
    if (
      requestData.isRepeated &&
      repeatConfig?.repeatType &&
      ["WEEKS", "MONTHS"].includes(repeatConfig.repeatType)
    )
      return { success: false, message: "לא ניתן לשמור שינויים לבקשה חוזרת" };

    const associationRequestedId = resolveAssociationRequestedId(
      associationRequested,
      requestData.associationRequestedId,
    );

    const expectedUpdatedAt = (requestData as RequestType).expectedUpdatedAt;

    if (requestData.id && expectedUpdatedAt) {
      const current = await repo(Opportunity).findId(requestData.id);
      if (
        current?.updatedAt &&
        new Date(current.updatedAt).getTime() !==
          new Date(expectedUpdatedAt).getTime()
      ) {
        return {
          success: false,
          code: "CONFLICT",
          message:
            "המשימה עודכנה על ידי מנהל אחר. יש לרענן ולנסות שוב.",
        };
      }
    }

    const managerId = manager?.id ?? requestData.managerId ?? "";
    const subCategoryId = await resolveSubCategoryIdForSave(
      requestData.subCategoryId,
    );

    const savePayload = {
      ...requestData,
      subCategoryId,
      associationRequestedId,
      managerId,
      notificationTemplates: requestData.notificationTemplates || undefined,
    };

    const approvalCtx = requestData.id
      ? {
          isNew: false as const,
          previousStatus: (await repo(Opportunity).findId(requestData.id))
            ?.status,
        }
      : { isNew: true as const };

    applyRequestApprovedAtForSave(savePayload, approvalCtx);

    const OK = await repo(Opportunity).save(savePayload);
    if (!OK) return { success: false, message: "קרתה תקלה בעת עדכון המשימה" };

    if (
      requestData.id &&
      !approvalCtx.isNew &&
      savePayload.status === "APPROVED" &&
      approvalCtx.previousStatus !== "APPROVED"
    ) {
      const savedParent = await repo(Opportunity).findId(requestData.id);
      if (savedParent?.isOpportunityGroupParent) {
        const approvedAt =
          (savePayload as { requestApprovedAt?: Date | null })
            .requestApprovedAt ?? new Date();
        await repo(Opportunity).updateMany({
          where: { parentOpportunityId: requestData.id, isDeleted: false },
          set: {
            status: "APPROVED",
            requestApprovedAt: approvedAt,
          },
        });
      }
    }

    if (selectedTeams?.length > 0) {
      const teamsResponse = await Opportunity.saveTeamsForOpportunity(
        requestData.id!,
        selectedTeams,
      );
      if (!teamsResponse.success) throw new Error(teamsResponse.message);
    }

    // Save categories if provided
    // Priority: selectedCategories > categories (from relation)
    const categoriesToSave =
      selectedCategories.length > 0
        ? selectedCategories
        : categories.map((oc) => (oc as any).category || oc).filter(Boolean);

    if (categoriesToSave.length > 0) {
      const saveCategoriesResponse =
        await Opportunity.saveCategoriesForOpportunity(
          requestData.id!,
          categoriesToSave,
        );

      if (!saveCategoriesResponse.success)
        throw new Error(saveCategoriesResponse.message);
    }

    // Save populations if provided
    // Priority: selectedPopulations > populations (from relation)
    const populationsToSave =
      selectedPopulations.length > 0
        ? selectedPopulations
        : populations.map((op) => (op as any).population || op).filter(Boolean);

    if (populationsToSave.length > 0) {
      const savePopulationsResponse =
        await Opportunity.savePopulationsForOpportunity(
          requestData.id!,
          populationsToSave,
        );

      if (!savePopulationsResponse.success)
        throw new Error(savePopulationsResponse.message);
    }

    if (requestData.parentOpportunityId) {
      await Opportunity.syncGroupParentFromChildren(
        requestData.parentOpportunityId,
      );
    } else if (requestData.isOpportunityGroupParent && requestData.id) {
      await Opportunity.syncGroupParentFromChildren(requestData.id);
    }

    if (requestData.id) {
      await syncOpportunityCoordinatesGeog(
        requestData.id,
        requestData.coordinates,
      );
    }

    return { success: true, message: "המשימה עודכנה בהצלחה" };
  }

  /**
   * Deletes an existing opportunity request.
   *
   * @param requestId - The ID of the opportunity request to be deleted.
   * @returns A promise resolving to a `RequestResponse` indicating success or failure.
   */
  @HyperBackendMethod({
    apiPrefix: "opportunity-methods",
    allowed: Allow.authenticated,
    overrideSecurity: true,
  })
  static async deleteRequest(
    requestId: string,
    reason?: (typeof REQUEST_DELETED_REASON)[number],
    options?: { skipNotifications?: boolean },
  ): Promise<RequestResponse> {
    const opportunity = await repo(Opportunity).findId(requestId, {
      include: {
        participantUsers: {
          where: { status: "APPROVED" },
          include: { association: { include: { user: true } } },
        },
        associationRequested: {
          include: {
            user: true,
          },
        },
      },
    });

    if (!opportunity) throw new Error(`Opportunity not found: ${requestId}`);
    if (opportunity.isDeleted) {
      return { success: true, message: "המשימה כבר בארכיון" };
    }

    if (opportunity.isOpportunityGroupParent) {
      const children = await repo(Opportunity).find({
        where: { parentOpportunityId: requestId, isDeleted: false },
      });
      const childIds = children.map((c) => c.id);
      throwIfAssignedVolunteers(
        await assignedVolunteerCounts([requestId, ...childIds]),
        true,
      );
    } else {
      throwIfAssignedVolunteers(await assignedVolunteerCounts([requestId]));
    }

    const isAidRequest =
      opportunity.status === "AID_REQUEST" &&
      !!opportunity.associationRequestedId;

    if (!options?.skipNotifications && isAidRequest) {
      const userRequested = opportunity.associationRequested;
      const hasContact =
        userRequested?.user?.phoneNumber || userRequested?.user?.email;
      if (userRequested?.user && hasContact) {
        await sendMessageToRecipients(
          {
            message: textBuilder()
              .line("הבקשה שלך נדחתה.")
              .newLine(1)
              .line(
                REQUEST_DELETED_REASON_MASSAGE[
                  reason || "REQUEST_NOT_APPROVED"
                ],
              )
              .newLine(1)
              .line(WEME_SUPPORT_WHATSAPP_CTA)
              .done(),
          },
          [
            {
              phoneNumber: userRequested.user.phoneNumber,
              email: userRequested.user.email,
            },
          ],
          ["whatsapp", "sms"],
        );
      }
    }

    // Notify approved volunteers when seeker cancels (push + SMS + in-app in parallel)
    if (!options?.skipNotifications && isAidRequest) {
      const feedLink = `${WEME_APP_BASE_URL}${buildFeedPath()}`;
      const approvedParticipants =
        opportunity.participantUsers?.filter((p) => p.status === "APPROVED") ??
        [];

      await Promise.all(
        approvedParticipants.map(async (participant) => {
          const user = participant.association?.user;
          if (!user?.id && !user?.phoneNumber) return;

          await sendTemplatedUserNotification({
            templateKey: NotificationTemplateKey.SEEKER_CANCELLED_REQUEST,
            placeholders: {
              user_name: user.name || "",
              feed_link: feedLink,
            },
            userId: user.id ?? undefined,
            phoneNumber: user.phoneNumber ?? undefined,
            channels: {
              push: !!user.id,
              sms: !!user.phoneNumber,
              inApp: !!user.id,
            },
            navigateTo: buildFeedPath(),
          });
        }),
      );
    }

    const deletedAt = new Date();
    await repo(Opportunity).update(requestId, {
      isDeleted: true,
      deletedAt,
      deletedReason: reason,
    });

    if (opportunity.isOpportunityGroupParent) {
      await repo(Opportunity).updateMany({
        where: { parentOpportunityId: requestId, isDeleted: false },
        set: { isDeleted: true, deletedAt },
      });
      return {
        success: true,
        message: ADMIN_GROUP_ARCHIVED_SUCCESS,
      };
    }

    if (opportunity.parentOpportunityId) {
      await Opportunity.syncGroupParentFromChildren(
        opportunity.parentOpportunityId,
      );
    }

    return { success: true, message: "המשימה הועברה לארכיון בהצלחה" };
  }

  @HyperBackendMethod({
    apiPrefix: "opportunity-methods",
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async restoreRequest(requestId: string): Promise<RequestResponse> {
    const opportunity = await repo(Opportunity).findId(requestId);
    if (!opportunity) throw new Error(`Opportunity not found: ${requestId}`);
    if (!opportunity.isDeleted) {
      return { success: true, message: "המשימה כבר פעילה" };
    }

    await repo(Opportunity).update(requestId, {
      isDeleted: false,
      deletedAt: undefined,
      deletedReason: undefined,
    });

    if (opportunity.isOpportunityGroupParent) {
      await repo(Opportunity).updateMany({
        where: { parentOpportunityId: requestId, isDeleted: true },
        set: {
          isDeleted: false,
          deletedAt: undefined,
          deletedReason: undefined,
        },
      });
      await Opportunity.syncGroupParentFromChildren(requestId);
      return {
        success: true,
        message: ADMIN_GROUP_RESTORED_SUCCESS,
      };
    }

    if (opportunity.parentOpportunityId) {
      await Opportunity.syncGroupParentFromChildren(
        opportunity.parentOpportunityId,
      );
    }

    return { success: true, message: "המשימה שוחזרה מהארכיון בהצלחה" };
  }

  /**
   * Deletes an existing repeated opportunities by one connected opportunity.
   *
   * @param requestToDelete - The opportunity connected to the repeated opportunities.
   * @returns A promise resolving to a `RequestResponse` indicating success or failure.
   */
  @HyperBackendMethod({
    apiPrefix: "opportunity-methods",
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async deleteRepeatedRequest(
    requestToDelete: RequestType,
  ): Promise<RequestResponse> {
    const { repeatedOpportunityId, ...request } = requestToDelete;

    const opportunities = await repo(Opportunity).find({
      where: { repeatedOpportunityId },
    });

    const toArchive = await repo(Opportunity).find({
      where: {
        startDate: { $gte: request.startDate },
        repeatedOpportunityId,
        isRepeated: true,
      },
    });

    throwIfAssignedVolunteers(
      await assignedVolunteerCounts(
        toArchive.map((opportunity) => opportunity.id),
      ),
    );

    const idsToArchive = toArchive.map((o) => o.id);
    if (idsToArchive.length > 0) {
      await repo(Opportunity).updateMany({
        where: { id: { $in: idsToArchive } },
        set: {
          isDeleted: true,
          deletedAt: new Date(),
        },
      });
    }

    return {
      success: true,
      message: "המשימות הועברו לארכיון בהצלחה",
      requestOpportunity: opportunities[0],
    };
  }

  // PUBLIC 👆
  // --------------
  // PRIVATE 👇

  /**
   * Creates a new repeated opportunity request based on the provided repeat configuration.
   * Generates multiple occurrences of the opportunity based on the repeat interval and days.
   *
   * @param request - The repeated opportunity request data.
   * @returns A promise resolving to a `RequestResponse` indicating success or failure.
   */
  @BackendOnly()
  private static async createRepeatedRequest({
    repeatConfig,
    selectedTeams = [],
    selectedCategories = [],
    selectedPopulations = [],
    manager,
    ...requestDetails
  }: RequestType): Promise<RequestResponse> {
    const managerId = manager?.id ?? requestDetails.managerId ?? "";
    const startDate = dayjs(new Date(requestDetails.startDate || ""));
    const endDate = dayjs(new Date(requestDetails.endDate || ""));

    if (!repeatConfig)
      throw new Error("'repeatConfig' is required for repeated requests");

    if (!["WEEKS", "MONTHS"].includes(repeatConfig.repeatType)) {
      throw new Error(
        `Invalid repeat type: ${repeatConfig.repeatType}. Only 'WEEKS' and 'MONTHS' are supported for repeated opportunities.`,
      );
    }

    // Find the organization
    const organization = requestDetails.organization;

    if (!organization)
      throw new Error("'organization' is required for repeated requests");

    const { days = [], repeatInterval, repeatType } = repeatConfig;

    // Calculate the last date of the repeated opportunity
    let lastDate = startDate;
    if (repeatType === "WEEKS") {
      lastDate = startDate.add(repeatInterval * 7, "day");
    } else {
      lastDate = startDate.add(repeatInterval, "month");
    }

    // Create the repeated opportunity
    const newRepeatedOpportunity = await repo(RepeatedOpportunity).insert({
      days,
      interval: repeatInterval,
      repeatType,
      lastDate: lastDate.toDate(),
    });

    const events: any[] = []; // Array to hold all events

    for (const selectedDay of days) {
      const dayIndex = WEEK_DAYS.indexOf(selectedDay);

      if (dayIndex === -1) {
        console.error(`Invalid day: ${selectedDay}`);
        continue;
      }

      const currentDate = dayjs(requestDetails.startDate);
      const currentDay = currentDate.day();
      let daysToAdd = (dayIndex - currentDay + 7) % 7;
      if (daysToAdd === 0) daysToAdd = 7;

      const nextOccurrenceDate = currentDate.add(daysToAdd, "day");

      const interval = Math.round(
        repeatType === "MONTHS" ? repeatInterval * 4.2 : repeatInterval,
      );

      delete requestDetails.repeatedOpportunityId;
      delete requestDetails?.id;

      for (let i = 0; i < interval; i++) {
        const eventStartDate = nextOccurrenceDate
          .add(i * 7, "day")
          .startOf("day")
          .set("hour", startDate.hour())
          .set("minute", startDate.minute());

        const eventEndDate = nextOccurrenceDate
          .add(i * 7, "day")
          .startOf("day")
          .set("hour", endDate.hour())
          .set("minute", endDate.minute());

        events.push({
          ...requestDetails,
          managerId,
          id:
            requestDetails.action === "duplicate"
              ? undefined
              : requestDetails.id,
          isClosed:
            requestDetails.action === "duplicate"
              ? false
              : requestDetails.isClosed,
          isFull:
            requestDetails.action === "duplicate"
              ? false
              : requestDetails.isFull,
          endDate: eventEndDate.toDate(),
          startDate: eventStartDate.toDate(),
          day: selectedDay,
          repeatedOpportunity: newRepeatedOpportunity,
          isRepeated: true,
        });
      }
    }

    for (const event of events) {
      applyRequestApprovedAtForSave(event, { isNew: true });
    }

    const requests = await repo(Opportunity).insert(events);

    if (!requests.length) throw new Error("קרתה תקלה בעת יצירת הבקשה החוזרת");

    await Promise.all(
      requests.map(async (request) => {
        const teamsResponse = await Opportunity.saveTeamsForOpportunity(
          request.id,
          selectedTeams,
        );

        if (!teamsResponse.success)
          throw new Error(
            teamsResponse.message + " " + request.id + ", " + request.title,
          );

        if (selectedCategories.length > 0) {
          const categoriesResponse =
            await Opportunity.saveCategoriesForOpportunity(
              request.id,
              selectedCategories,
            );
          if (!categoriesResponse.success)
            throw new Error(categoriesResponse.message);
        }

        if (selectedPopulations.length > 0) {
          const populationsResponse =
            await Opportunity.savePopulationsForOpportunity(
              request.id,
              selectedPopulations,
            );
          if (!populationsResponse.success)
            throw new Error(populationsResponse.message);
        }
      }),
    );

    return {
      success: true,
      message: "המשימות נוצרו בהצלחה",
      requestOpportunity: requests[0],
    };
  }

  /**
   * Updates an existing repeated opportunity request.
   * Handles updates for a single occurrence or all occurrences of the repeated request.
   *
   * @param request - The repeated opportunity request data to be updated.
   * @returns A promise resolving to a `RequestResponse` indicating success or failure.
   */
  @BackendOnly()
  private static async updateRepeatedRequest(
    request: RequestType,
  ): Promise<RequestResponse> {
    const {
      selectedTeams = [],
      selectedCategories = [],
      selectedPopulations = [],
      editRepeatedType,
      manager,
      ...requestDetails
    } = request;

    const managerId = manager?.id ?? requestDetails.managerId ?? "";

    const repeatedCount = await repo(Opportunity).count({
      repeatedOpportunityId: requestDetails.repeatedOpportunityId,
    });

    if (repeatedCount === 0)
      throw new Error("No repeated opportunities found for this request");

    if (repeatedCount === 1)
      await repo(RepeatedOpportunity).delete(
        requestDetails.repeatedOpportunityId!,
      );

    if (editRepeatedType === "one") {
      const beforeSave = await repo(Opportunity).findId(requestDetails.id!);
      const onePayload = {
        ...requestDetails,
        managerId,
        repeatedOpportunityId: "",
        isRepeated: false,
        day: WEEK_DAYS[
          requestDetails.startDate
            ? new Date(requestDetails.startDate).getDay()
            : new Date().getDay()
        ],
      };
      applyRequestApprovedAtForSave(onePayload, {
        isNew: false,
        previousStatus: beforeSave?.status,
      });
      const requestOpportunity = await repo(Opportunity).save(onePayload);
      const teamsResponse = await Opportunity.saveTeamsForOpportunity(
        requestDetails.id!,
        selectedTeams,
      );
      if (!teamsResponse.success)
        throw new Error(
          teamsResponse.message +
            " " +
            requestDetails.id +
            ", " +
            requestDetails.title,
        );

      if (selectedCategories.length > 0) {
        const categoriesResponse =
          await Opportunity.saveCategoriesForOpportunity(
            requestDetails.id!,
            selectedCategories,
          );
        if (!categoriesResponse.success)
          throw new Error(categoriesResponse.message);
      }

      if (selectedPopulations.length > 0) {
        const populationsResponse =
          await Opportunity.savePopulationsForOpportunity(
            requestDetails.id!,
            selectedPopulations,
          );
        if (!populationsResponse.success)
          throw new Error(populationsResponse.message);
      }

      return {
        success: true,
        message: "המשימה עודכנה בהצלחה",
        requestOpportunity,
      };
    } else {
      const {
        id,
        startDate,
        endDate,
        day,
        isRepeated,
        repeatedOpportunityId,
        repeatedOpportunity,
        ...fieldsToUpdate
      } = requestDetails;

      // Extract hours and minutes from the new start and end dates
      const newStartDate = dayjs(startDate);
      const newEndDate = dayjs(endDate);
      const startHour = newStartDate.hour();
      const startMinute = newStartDate.minute();
      const endHour = newEndDate.hour();
      const endMinute = newEndDate.minute();

      // Get all opportunities that need to be updated
      const opportunities = await repo(Opportunity).find({
        where: { repeatedOpportunityId },
      });

      // Create an array of updated opportunities to save in bulk
      const updatedOpportunities = opportunities.map((opportunity) => {
        // Preserve the original date but update the hours and minutes
        const updatedStartDate = dayjs(opportunity.startDate)
          .set("hour", startHour)
          .set("minute", startMinute)
          .toDate();

        const updatedEndDate = dayjs(opportunity.endDate)
          .set("hour", endHour)
          .set("minute", endMinute)
          .toDate();

        // Update with all properties that need to change
        const merged = {
          ...opportunity,
          ...fieldsToUpdate,
          startDate: updatedStartDate,
          endDate: updatedEndDate,
        };
        applyRequestApprovedAtForSave(merged, {
          isNew: false,
          previousStatus: opportunity.status,
        });
        return merged;
      });

      // Save all opportunities in bulk
      await repo(Opportunity).save(updatedOpportunities);

      const updatedRequests = await repo(Opportunity).find({
        where: { repeatedOpportunityId },
      });

      await Promise.all(
        updatedRequests.map(async (request) => {
          const teamsResponse = await Opportunity.saveTeamsForOpportunity(
            request.id,
            selectedTeams,
          );

          if (!teamsResponse.success)
            throw new Error(
              teamsResponse.message + " " + request.id + ", " + request.title,
            );

          if (selectedCategories.length > 0) {
            const categoriesResponse =
              await Opportunity.saveCategoriesForOpportunity(
                request.id,
                selectedCategories,
              );
            if (!categoriesResponse.success)
              throw new Error(categoriesResponse.message);
          }

          if (selectedPopulations.length > 0) {
            const populationsResponse =
              await Opportunity.savePopulationsForOpportunity(
                request.id,
                selectedPopulations,
              );
            if (!populationsResponse.success)
              throw new Error(populationsResponse.message);
          }
        }),
      );

      return {
        success: true,
        message: "המשימה עודכנה בהצלחה",
        requestOpportunity: updatedRequests[0],
      };
    }
  }

  /**
   * Saves the selected teams for a specific opportunity.
   * Deletes existing team associations and creates new ones.
   *
   * @param opportunityId - The ID of the opportunity.
   * @param selectedTeams - The list of teams to associate with the opportunity.
   * @returns A promise resolving to a `RequestResponse` indicating success or failure.
   */
  @BackendOnly()
  private static async saveTeamsForOpportunity(
    opportunityId: string,
    selectedTeams: Team[] = [],
  ): Promise<RequestResponse> {
    if (!opportunityId)
      throw new Error("opportunityId is required in creating teams");

    await repo(TeamOpportunity).deleteMany({
      where: { opportunityId },
    });

    const oppTeams = await repo(TeamOpportunity).insert(
      selectedTeams.map((team) => ({
        teamId: team.id,
        opportunityId,
      })),
    );

    if (!oppTeams.length)
      return { success: false, message: "קרתה תקלה בעת יצירת הקבוצות" };
    return { success: true, message: "" };
  }

  @BackendOnly()
  private static async saveSkillsForOpportunity(
    opportunityId: string,
    parentSkills: OpportunitySkill[] = [],
  ): Promise<RequestResponse> {
    if (!opportunityId) {
      throw new Error("opportunityId is required in creating skills");
    }

    await repo(OpportunitySkill).deleteMany({
      where: { opportunityId },
    });

    if (parentSkills.length === 0) {
      return { success: true, message: "" };
    }

    const skillIds = parentSkills
      .map((os) => (os as { skill?: { id: string } }).skill?.id ?? os.skillId)
      .filter(Boolean);

    if (skillIds.length === 0) {
      return { success: true, message: "" };
    }

    const oppSkills = await repo(OpportunitySkill).insert(
      skillIds.map((skillId) => ({
        skillId,
        opportunityId,
      })),
    );

    if (!oppSkills.length) {
      return { success: false, message: "קרתה תקלה בעת יצירת המיומנויות" };
    }

    return { success: true, message: "" };
  }

  /**
   * Saves the selected categories for a specific opportunity.
   * Deletes existing category associations and creates new ones.
   *
   * @param opportunityId - The ID of the opportunity.
   * @param selectedCategories - The list of categories to associate with the opportunity.
   *                             Can be Category objects, objects with id property, or category ID strings.
   * @returns A promise resolving to a `RequestResponse` indicating success or failure.
   */
  @BackendOnly()
  private static async saveCategoriesForOpportunity(
    opportunityId: string,
    selectedCategories: CategorySelection[] = [],
  ): Promise<RequestResponse> {
    if (!opportunityId)
      throw new Error("opportunityId is required in creating categories");

    // If no categories provided, return success (allowing removal of all categories)
    if (selectedCategories.length === 0) return { success: true, message: "" };

    const requestedIds = [
      ...new Set(
        selectedCategories
          .map((category) => readCategoryId(category))
          .filter((id): id is string => Boolean(id)),
      ),
    ];
    const existing =
      requestedIds.length > 0
        ? await repo(Category).find({ where: { id: { $in: requestedIds } } })
        : [];
    const existingIds = new Set(existing.map((category) => category.id));
    const categoryIds = requestedIds.filter((id) => existingIds.has(id));

    if (categoryIds.length === 0) {
      await repo(OpportunityCategory).deleteMany({
        where: { opportunityId },
      });
      return { success: true, message: "" };
    }

    // Delete existing category associations only after the new ids are valid.
    await repo(OpportunityCategory).deleteMany({
      where: { opportunityId },
    });

    // Create new category associations
    const oppCategories = await repo(OpportunityCategory).insert(
      categoryIds.map((categoryId) => ({
        categoryId,
        opportunityId,
      })),
    );

    if (!oppCategories.length)
      return {
        success: false,
        message: "קרתה תקלה בעת יצירת הקטגוריות",
      };

    return { success: true, message: "" };
  }

  /**
   * Saves the selected populations for a specific opportunity.
   * Deletes existing population associations and creates new ones.
   *
   * @param opportunityId - The ID of the opportunity.
   * @param selectedPopulations - The list of populations to associate with the opportunity.
   *                             Can be Population objects, objects with id property, or population ID strings.
   * @returns A promise resolving to a `RequestResponse` indicating success or failure.
   */
  @BackendOnly()
  private static async savePopulationsForOpportunity(
    opportunityId: string,
    selectedPopulations: (Population | { id: string } | string)[] = [],
  ): Promise<RequestResponse> {
    if (!opportunityId)
      throw new Error("opportunityId is required in creating populations");

    // Delete existing population associations
    await repo(OpportunityPopulation).deleteMany({
      where: { opportunityId },
    });

    // If no populations provided, return success (allowing removal of all populations)
    if (selectedPopulations.length === 0) return { success: true, message: "" };

    // Extract population IDs from various input formats
    const populationIds = selectedPopulations.map((population) => {
      if (typeof population === "string") return population;
      return (population as Population).id || (population as { id: string }).id;
    });

    // Create new population associations
    const oppPopulations = await repo(OpportunityPopulation).insert(
      populationIds.map((populationId) => ({
        populationId,
        opportunityId,
      })),
    );

    if (!oppPopulations.length)
      return {
        success: false,
        message: "קרתה תקלה בעת יצירת האוכלוסיות",
      };

    return { success: true, message: "" };
  }

  @HyperBackendMethod({
    allowed: Allow.authenticated,
    overrideSecurity: true,
  })
  public static async getAllParticipantsUsers(opportunityId: string) {
    const users = await userRepo.find({
      where: User.hyperFilter({
        associations: {
          $some: {
            $or: [
              {
                opportunities: {
                  $some: {
                    opportunity: {
                      $id: opportunityId,
                    },
                  },
                },
              },
              {
                teams: {
                  $some: {
                    team: {
                      $is: {
                        opportunities: {
                          $some: {
                            opportunityId,
                          },
                        },
                      },
                    },
                  },
                },
              },
            ],
          },
        },
      }),
    });

    return users.map((v) => v.id);
  }

  /**
   * Sends "request performance report" SMS to volunteers and seekers for the given opportunities.
   * Only sends to people who have not yet submitted a report (prevents sending twice).
   * For all selected opportunities (no filter on hasTime or isReportRequired).
   * Remult-only: uses repo() and MessageController.
   */
  @HyperBackendMethod({
    apiPrefix: "opportunity-methods",
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async sendReportRequestForOpportunities(opportunityIds: string[]) {
    if (areOpportunitySurveysDisabled()) {
      return {
        success: false,
        message: "שליחת בקשות דיווח ביצוע כבויה זמנית",
        sentVolunteers: 0,
        sentSeekers: 0,
      };
    }

    if (!opportunityIds?.length) {
      return {
        success: true,
        message: "לא נבחרו משימות",
        sentVolunteers: 0,
        sentSeekers: 0,
      };
    }

    const existingReports = await opportunityReportRepo.find({
      where: { opportunityId: { $in: opportunityIds } },
      select: { opportunityId: true, associationId: true },
    });
    const reportedSet = new Set(
      existingReports.map((r) => `${r.opportunityId}:${r.associationId}`),
    );

    const participants = await repo(OpportunityParticipant).find({
      where: {
        opportunityId: { $in: opportunityIds },
        status: "APPROVED",
        hasDeclinedSurvey: false,
      },
      include: {
        association: { include: { user: true } },
        opportunity: true,
      },
    });

    const volunteersNeedReport = participants.filter(
      (p) =>
        (!p.groupId || p.groupId === "") &&
        !reportedSet.has(`${p.opportunityId}:${p.associationId}`),
    );

    const opportunitiesWithSeeker = await repo(Opportunity).find({
      where: {
        id: { $in: opportunityIds },
        associationRequestedId: { $ne: "" },
      },
      include: {
        associationRequested: { include: { user: true } },
      },
    });

    const seekersNeedReport = opportunitiesWithSeeker.filter(
      (opp) =>
        opp.associationRequestedId &&
        !reportedSet.has(`${opp.id}:${opp.associationRequestedId}`),
    );

    let sentVolunteers = 0;
    for (const ep of volunteersNeedReport) {
      const user = ep.association?.user;
      const opportunityId = ep.opportunity?.id;
      if (!opportunityId || (!user?.id && !user?.phoneNumber)) continue;
      const surveyNavigateTo = buildOpportunityPath(opportunityId, {
        action: NOTIFICATION_ACTION_SURVEY,
      });
      const feedbackLink = `${WEME_APP_BASE_URL}${surveyNavigateTo}`;
      await sendTemplatedUserNotification({
        templateKey: NotificationTemplateKey.POST_EVENT_FEEDBACK,
        placeholders: {
          user_name: user.name || "",
          request_title: ep.opportunity?.title ?? "התנדבות",
          feedback_link: feedbackLink,
        },
        userId: user.id ?? undefined,
        phoneNumber: user.phoneNumber ?? undefined,
        channels: {
          push: !!user.id,
          sms: !!user.phoneNumber,
          inApp: !!user.id,
        },
        navigateTo: surveyNavigateTo,
      });
      sentVolunteers++;
    }

    let sentSeekers = 0;
    for (const opp of seekersNeedReport) {
      const user = opp.associationRequested?.user;
      if (!opp.id || (!user?.id && !user?.phoneNumber)) continue;
      const surveyNavigateTo = buildOpportunityPath(opp.id, {
        action: NOTIFICATION_ACTION_SURVEY,
      });
      const feedbackLink = `${WEME_APP_BASE_URL}${surveyNavigateTo}`;
      await sendTemplatedUserNotification({
        templateKey: NotificationTemplateKey.SEEKER_POST_EVENT_FEEDBACK,
        placeholders: {
          user_name: user?.name ?? "",
          request_title: opp.title ?? "בקשת סיוע",
          feedback_link: feedbackLink,
        },
        userId: user.id ?? undefined,
        phoneNumber: user.phoneNumber ?? undefined,
        channels: {
          push: !!user.id,
          sms: !!user.phoneNumber,
          inApp: !!user.id,
        },
        navigateTo: surveyNavigateTo,
      });
      sentSeekers++;
    }

    const message = `נשלחו ${sentVolunteers} הודעות למתנדבים ו-${sentSeekers} הודעות למבקשי סיוע.`;

    return {
      success: true,
      message,
      sentVolunteers,
      sentSeekers,
    };
  }

  @HyperBackendMethod({
    apiPrefix: "opportunity-methods",
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async createGroupChildSlot(
    parentId: string,
    slot: Pick<
      Opportunity,
      | "startDate"
      | "endDate"
      | "hasTime"
      | "hasStartTime"
      | "timeDescription"
      | "location"
      | "coordinates"
      | "amount"
      | "day"
      | "isRemote"
    >,
  ): Promise<{ success: boolean; message: string; childId?: string }> {
    const parent = await repo(Opportunity).findId(parentId, {
      include: {
        categories: { include: { category: true } },
        populations: { include: { population: true } },
        teams: { include: { team: true } },
        requiredSkills: { include: { skill: true } },
      },
    });
    if (!parent?.isOpportunityGroupParent) {
      return { success: false, message: "הקבוצה לא נמצאה" };
    }

    const scheduleAnchor = slot.hasTime
      ? slot.hasStartTime
        ? slot.startDate
        : slot.endDate
      : null;
    const lastCallToVolunteers = scheduleAnchor
      ? dayjs(scheduleAnchor).subtract(3, "hour").toDate()
      : parent.lastCallToVolunteers;

    const child = repo(Opportunity).create({
      ...pickGroupChildBaseFields(parent),
      ...slot,
      lastCallToVolunteers,
      id: undefined,
      isOpportunityGroupParent: false,
      parentOpportunityId: parentId,
      isHidden: true,
      isClosed: false,
      isFull: false,
      isRepeated: false,
      repeatedOpportunityId: undefined,
    });

    applyRequestApprovedAtForSave(child, { isNew: true });
    const saved = await repo(Opportunity).save(child);
    if (!saved) {
      return { success: false, message: ADMIN_GROUP_CREATE_INSTANCE_ERROR };
    }

    if (!slot.isRemote) {
      await syncOpportunityCoordinatesGeog(saved.id, saved.coordinates);
    }

    const selectedTeams =
      parent.teams
        ?.map((t) => (t as { team?: Team }).team)
        .filter((team): team is Team => !!team) ?? [];
    if (selectedTeams.length > 0) {
      const teamsResponse = await Opportunity.saveTeamsForOpportunity(
        saved.id,
        selectedTeams,
      );
      if (!teamsResponse.success) {
        throw new Error(teamsResponse.message);
      }
    }

    const categories =
      parent.categories?.map((c) => (c as { category?: { id: string } }).category ?? c) ??
      [];
    const populations =
      parent.populations?.map((p) => (p as { population?: { id: string } }).population ?? p) ??
      [];

    if (categories.length > 0) {
      await Opportunity.saveCategoriesForOpportunity(
        saved.id,
        categories as Parameters<
          typeof Opportunity.saveCategoriesForOpportunity
        >[1],
      );
    }
    if (populations.length > 0) {
      await Opportunity.savePopulationsForOpportunity(
        saved.id,
        populations as Parameters<
          typeof Opportunity.savePopulationsForOpportunity
        >[1],
      );
    }

    if (parent.requiredSkills?.length) {
      const skillsResponse = await Opportunity.saveSkillsForOpportunity(
        saved.id,
        parent.requiredSkills,
      );
      if (!skillsResponse.success) {
        throw new Error(skillsResponse.message);
      }
    }

    await Opportunity.syncGroupParentFromChildren(parentId);

    return {
      success: true,
      message: ADMIN_GROUP_CREATE_INSTANCE_SUCCESS,
      childId: saved.id,
    };
  }

  @HyperBackendMethod({
    apiPrefix: "opportunity-methods",
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
    transactional: true,
  })
  static async bulkUpdateGroupChildSlots(
    parentId: string,
    childIds: string[],
    patch: GroupChildBulkUpdatePatchInput,
  ): Promise<{ success: boolean; message: string; updated: number }> {
    const uniqueIds = [...new Set((childIds ?? []).filter(Boolean))];
    if (uniqueIds.length === 0) {
      return { success: false, message: "יש לבחור לפחות מופע אחד", updated: 0 };
    }
    if (uniqueIds.length > GROUP_INSTANCE_BULK_MAX) {
      return {
        success: false,
        message: `ניתן לערוך עד ${GROUP_INSTANCE_BULK_MAX} מופעים בפעולה אחת`,
        updated: 0,
      };
    }

    const parent = await repo(Opportunity).findId(parentId);
    if (!parent?.isOpportunityGroupParent) {
      return { success: false, message: "הקבוצה לא נמצאה", updated: 0 };
    }

    const patchError = validateGroupChildBulkUpdatePatch(patch);
    if (patchError) {
      return { success: false, message: patchError, updated: 0 };
    }

    const relations = selectGroupChildBulkRelations(patch);
    const relationError = await Opportunity.validateBulkRelationIds(
      parent.organizationId,
      relations,
    );
    if (relationError) {
      return { success: false, message: relationError, updated: 0 };
    }

    const children = await repo(Opportunity).find({
      where: { parentOpportunityId: parentId, isDeleted: false },
    });
    const childrenById = new Map(children.map((child) => [child.id, child]));
    const missing = uniqueIds.filter((id) => !childrenById.has(id));
    if (missing.length > 0) {
      return {
        success: false,
        message: "חלק מהמופעים שנבחרו אינם שייכים לקבוצה",
        updated: 0,
      };
    }

    const applyLocation = patch.applyLocation === true;
    let parentUpdate: ReturnType<typeof pickParentBulkUpdate> = {};

    for (const childId of uniqueIds) {
      const child = childrenById.get(childId);
      const fields = buildGroupChildBulkUpdateFields(
        patch,
        parent.lastCallToVolunteers,
        {
          lastCallToVolunteers: child?.lastCallToVolunteers,
          day: child?.day,
        },
      );
      const update = selectGroupChildBulkUpdateWrite(fields, patch);
      parentUpdate = pickParentBulkUpdate(update);
      if (Object.keys(update).length > 0) {
        await repo(Opportunity).update(childId, update);
      }
      if (applyLocation) {
        await syncOpportunityCoordinatesGeog(childId, fields.coordinates);
      }
      await Opportunity.replaceBulkRelations(childId, relations);
    }

    if (Object.keys(parentUpdate).length > 0) {
      await repo(Opportunity).update(parentId, parentUpdate);
    }
    await Opportunity.replaceBulkRelations(parentId, relations);

    await Opportunity.syncGroupParentFromChildren(parentId);

    return {
      success: true,
      message:
        uniqueIds.length === 1
          ? "המופע עודכן בהצלחה"
          : `${uniqueIds.length} מופעים עודכנו בהצלחה`,
      updated: uniqueIds.length,
    };
  }

  @HyperBackendMethod({
    apiPrefix: "opportunity-methods",
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
    transactional: true,
  })
  static async bulkUpdateSelected(
    ids: string[],
    patch: GroupChildBulkUpdatePatchInput,
  ): Promise<{ success: boolean; message: string; updated: number }> {
    const uniqueIds = [...new Set((ids ?? []).filter(Boolean))];
    if (uniqueIds.length === 0) {
      return { success: false, message: "יש לבחור לפחות משימה אחת", updated: 0 };
    }
    if (uniqueIds.length > GROUP_INSTANCE_BULK_MAX) {
      return {
        success: false,
        message: `ניתן לערוך עד ${GROUP_INSTANCE_BULK_MAX} משימות בפעולה אחת`,
        updated: 0,
      };
    }

    const patchError = validateGroupChildBulkUpdatePatch(patch);
    if (patchError) {
      return { success: false, message: patchError, updated: 0 };
    }

    const selected = await repo(Opportunity).find({
      where: { id: { $in: uniqueIds } },
    });
    if (selected.length !== uniqueIds.length) {
      return {
        success: false,
        message: "חלק מהמשימות שנבחרו לא נמצאו",
        updated: 0,
      };
    }

    const organizationIds = new Set(selected.map((row) => row.organizationId));
    if (organizationIds.size !== 1) {
      return {
        success: false,
        message: "אפשר לערוך משימות מארגון אחד בכל פעם",
        updated: 0,
      };
    }

    const relations = selectGroupChildBulkRelations(patch);
    const relationError = await Opportunity.validateBulkRelationIds(
      selected[0].organizationId,
      relations,
    );
    if (relationError) {
      return { success: false, message: relationError, updated: 0 };
    }

    const parentRows = selected.filter((row) => row.isOpportunityGroupParent);
    const selectedParentIds = parentRows.map((row) => row.id);
    const childrenOfParents =
      selectedParentIds.length > 0
        ? await repo(Opportunity).find({
            where: {
              parentOpportunityId: { $in: selectedParentIds },
              isDeleted: false,
            },
          })
        : [];

    const rowsToUpdate = new Map<string, Opportunity>();
    for (const row of selected) {
      if (!row.isOpportunityGroupParent) rowsToUpdate.set(row.id, row);
    }
    for (const child of childrenOfParents) rowsToUpdate.set(child.id, child);
    for (const parent of parentRows) {
      const hasChild = childrenOfParents.some(
        (child) => child.parentOpportunityId === parent.id,
      );
      if (!hasChild) rowsToUpdate.set(parent.id, parent);
    }

    const parentsById = new Map(parentRows.map((row) => [row.id, row]));
    const parentIdsToSync = new Set(selectedParentIds);
    for (const row of rowsToUpdate.values()) {
      if (row.parentOpportunityId) parentIdsToSync.add(row.parentOpportunityId);
    }
    const missingParentIds = [...parentIdsToSync].filter(
      (id) => !parentsById.has(id),
    );
    if (missingParentIds.length > 0) {
      const extraParents = await repo(Opportunity).find({
        where: { id: { $in: missingParentIds } },
      });
      for (const parent of extraParents) parentsById.set(parent.id, parent);
    }

    for (const row of rowsToUpdate.values()) {
      const parent = row.parentOpportunityId
        ? parentsById.get(row.parentOpportunityId)
        : undefined;
      const parentLastCall =
        parent?.lastCallToVolunteers ??
        row.lastCallToVolunteers ??
        new Date();
      const fields = buildGroupChildBulkUpdateFields(patch, parentLastCall, {
        lastCallToVolunteers: row.lastCallToVolunteers,
        day: row.day,
      });
      const update = selectGroupChildBulkUpdateWrite(fields, patch);
      if (Object.keys(update).length > 0) {
        await repo(Opportunity).update(row.id, update);
      }
      if (patch.applyLocation === true) {
        await syncOpportunityCoordinatesGeog(row.id, fields.coordinates);
      }
      await Opportunity.replaceBulkRelations(row.id, relations);
    }

    for (const parentId of parentIdsToSync) {
      const parent = parentsById.get(parentId);
      const parentLastCall = parent?.lastCallToVolunteers ?? new Date();
      const fields = buildGroupChildBulkUpdateFields(patch, parentLastCall, {
        lastCallToVolunteers: parent?.lastCallToVolunteers,
        day: parent?.day,
      });
      const update = selectGroupChildBulkUpdateWrite(fields, patch);
      const parentUpdate = selectedParentIds.includes(parentId)
        ? update
        : pickParentBulkUpdate(update);
      if (Object.keys(parentUpdate).length > 0) {
        await repo(Opportunity).update(parentId, parentUpdate);
      }
      await Opportunity.replaceBulkRelations(parentId, relations);
      await Opportunity.syncGroupParentFromChildren(parentId);
    }

    return {
      success: true,
      message:
        uniqueIds.length === 1
          ? "המשימה עודכנה בהצלחה"
          : `${uniqueIds.length} משימות עודכנו בהצלחה`,
      updated: uniqueIds.length,
    };
  }

  @BackendOnly()
  private static async validateBulkRelationIds(
    organizationId: string,
    relations: GroupChildBulkRelations,
  ): Promise<string | null> {
    if (relations.teamIds && relations.teamIds.length > 0) {
      const rows = await repo(Team).find({
        where: { id: { $in: relations.teamIds }, organizationId },
        select: { id: true },
      });
      if (rows.length !== relations.teamIds.length) {
        return "חלק מהפרויקטים לא נמצאו בארגון";
      }
    }
    if (relations.categoryIds && relations.categoryIds.length > 0) {
      const rows = await repo(Category).find({
        where: { id: { $in: relations.categoryIds }, organizationId },
        select: { id: true },
      });
      if (rows.length !== relations.categoryIds.length) {
        return "חלק מהקטגוריות לא נמצאו בארגון";
      }
    }
    if (relations.populationIds && relations.populationIds.length > 0) {
      const rows = await repo(Population).find({
        where: { id: { $in: relations.populationIds }, organizationId },
        select: { id: true },
      });
      if (rows.length !== relations.populationIds.length) {
        return "חלק מהאוכלוסיות לא נמצאו בארגון";
      }
    }
    return null;
  }

  @BackendOnly()
  private static async replaceBulkRelations(
    opportunityId: string,
    relations: GroupChildBulkRelations,
  ) {
    if (relations.teamIds !== undefined) {
      await repo(TeamOpportunity).deleteMany({ where: { opportunityId } });
      if (relations.teamIds.length > 0) {
        await repo(TeamOpportunity).insert(
          relations.teamIds.map((teamId) => ({ teamId, opportunityId })),
        );
      }
    }
    if (relations.categoryIds !== undefined) {
      await repo(OpportunityCategory).deleteMany({ where: { opportunityId } });
      if (relations.categoryIds.length > 0) {
        await repo(OpportunityCategory).insert(
          relations.categoryIds.map((categoryId) => ({
            categoryId,
            opportunityId,
          })),
        );
      }
    }
    if (relations.populationIds !== undefined) {
      await repo(OpportunityPopulation).deleteMany({
        where: { opportunityId },
      });
      if (relations.populationIds.length > 0) {
        await repo(OpportunityPopulation).insert(
          relations.populationIds.map((populationId) => ({
            populationId,
            opportunityId,
          })),
        );
      }
    }
  }

  @HyperBackendMethod({
    apiPrefix: "opportunity-methods",
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async bulkGenerateGroupChildSlots(
    parentId: string,
    payload: {
      slots: GroupChildSlotInput[];
      skipDuplicates?: boolean;
    },
  ): Promise<{
    success: boolean;
    message: string;
    created: number;
    skipped: number;
    failed: { index: number; error: string }[];
  }> {
    if (!payload.slots?.length) {
      return {
        success: false,
        message: "לא נמצאו מופעים ליצירה",
        created: 0,
        skipped: 0,
        failed: [],
      };
    }

    const skipDuplicates = payload.skipDuplicates !== false;
    const parent = await OpportunityMethods.loadGroupParentForChildCreate(
      parentId,
    );
    if (!parent) {
      return {
        success: false,
        message: "הקבוצה לא נמצאה",
        created: 0,
        skipped: 0,
        failed: [],
      };
    }

    const existingChildren = await repo(Opportunity).find({
      where: { parentOpportunityId: parentId, isDeleted: false },
    });
    const existingKeys = buildExistingSlotKeys(existingChildren);

    let slotsToCreate = payload.slots
      .map((slot) => OpportunityMethods.normalizeBulkGroupChildSlot(slot))
      .filter((slot): slot is GroupChildSlotInput => slot !== null);
    let skipped = payload.slots.length - slotsToCreate.length;

    if (skipDuplicates) {
      const filtered: GroupChildSlotInput[] = [];
      const batchKeys = new Set<string>();
      for (const slot of slotsToCreate) {
        const key = groupInstanceIdentityKey(slot);
        if (!key || key === "|") {
          skipped++;
          continue;
        }
        if (existingKeys.has(key) || batchKeys.has(key)) {
          skipped++;
          continue;
        }
        batchKeys.add(key);
        filtered.push(slot);
      }
      slotsToCreate = filtered;
    }

    if (slotsToCreate.length > GROUP_INSTANCE_BULK_MAX) {
      return {
        success: false,
        message: `ניתן ליצור עד ${GROUP_INSTANCE_BULK_MAX} מופעים בפעולה אחת`,
        created: 0,
        skipped,
        failed: [],
      };
    }

    if (slotsToCreate.length === 0) {
      return {
        success: true,
        message:
          skipped > 0
            ? "כל המופעים כבר קיימים - לא נוצרו מופעים חדשים"
            : "לא נמצאו מופעים ליצירה",
        created: 0,
        skipped,
        failed: [],
      };
    }

    const resolvedSlots: GroupChildSlotInput[] = [];
    for (const slot of slotsToCreate) {
      if (
        slot.isRemote ||
        !slot.location?.trim() ||
        hasValidCoordinates(slot.coordinates)
      ) {
        resolvedSlots.push(slot);
        continue;
      }
      const geo = await GooglePlacesController.geocodeAddress(slot.location);
      if (!geo) {
        return {
          success: false,
          message: "לא הצלחנו לאתר את המיקום - בחרו מיקום מהרשימה",
          created: 0,
          skipped,
          failed: [],
        };
      }
      resolvedSlots.push({
        ...slot,
        location: geo.formattedAddress,
        coordinates: { lat: geo.lat, lng: geo.lng },
      });
    }
    slotsToCreate = resolvedSlots;

    let created = 0;
    const failed: { index: number; error: string }[] = [];

    const now = new Date();

    for (let index = 0; index < slotsToCreate.length; index++) {
      const slot = slotsToCreate[index];
      const slotError = OpportunityMethods.validateBulkGroupChildSlot(slot, now);
      if (slotError) {
        failed.push({ index, error: slotError });
        continue;
      }
      try {
        const result = await OpportunityMethods.createGroupChildSlotInternal(
          parent,
          parentId,
          slot,
        );
        if (result.success) {
          created++;
        } else {
          failed.push({
            index,
            error: result.message,
          });
        }
      } catch (error) {
        failed.push({
          index,
          error:
            error instanceof Error ? error.message : "קרתה תקלה ביצירת המופע",
        });
      }
    }

    if (created > 0) {
      await Opportunity.syncGroupParentFromChildren(parentId);
    }

    const success = created > 0 && failed.length === 0;
    let message: string;
    if (created === 0 && failed.length > 0) {
      message = "לא הצלחנו ליצור מופעים";
    } else if (failed.length > 0) {
      message = `נוצרו ${created} מופעים, ${failed.length} נכשלו`;
    } else if (skipped > 0) {
      message = `נוצרו ${created} מופעים (${skipped} דולגו - כבר קיימים)`;
    } else {
      message = `נוצרו ${created} מופעים בהצלחה`;
    }

    return {
      success,
      message,
      created,
      skipped,
      failed,
    };
  }

  private static normalizeBulkGroupChildSlot(
    slot: GroupChildSlotInput,
  ): GroupChildSlotInput | null {
    if (!slot) return null;

    if (slot.hasTime === false) {
      const timeDescription = slot.timeDescription?.trim() ?? "";
      if (!timeDescription) return null;
      return {
        ...slot,
        hasTime: false,
        hasStartTime: true,
        startDate: null,
        endDate: null,
        timeDescription,
      };
    }

    if (!slot.startDate || !slot.endDate) return null;
    const startDate = new Date(slot.startDate);
    const endDate = new Date(slot.endDate);
    if (
      !Number.isFinite(startDate.getTime()) ||
      !Number.isFinite(endDate.getTime())
    ) {
      return null;
    }
    if (endDate.getTime() <= startDate.getTime()) return null;
    return {
      ...slot,
      hasTime: true,
      hasStartTime: slot.hasStartTime !== false,
      startDate,
      endDate,
      timeDescription: "",
    };
  }

  private static validateBulkGroupChildSlot(
    slot: GroupChildSlotInput,
    now: Date,
  ): string | null {
    if (slot.hasTime === false) {
      if (!slot.timeDescription?.trim()) {
        return "יש למלא תיאור זמן";
      }
    } else {
      if (!slot.hasTime || !slot.hasStartTime) {
        return "מופע ללא זמן מוגדר";
      }
      if (!slot.startDate || slot.startDate.getTime() < now.getTime()) {
        return "תאריך ההתחלה לא יכול להיות בעבר";
      }
    }
    if (!slot.isRemote && !slot.location?.trim()) {
      return "יש למלא מיקום";
    }
    if (!slot.isRemote && !hasValidCoordinates(slot.coordinates)) {
      return "יש לבחור מיקום תקין";
    }
    if ((slot.amount ?? 0) < 1) {
      return "יש להזין לפחות מקום אחד";
    }
    return null;
  }

  private static async loadGroupParentForChildCreate(
    parentId: string,
  ): Promise<GroupParentWithRelations | null> {
    const parent = await repo(Opportunity).findId(parentId, {
      include: {
        categories: { include: { category: true } },
        populations: { include: { population: true } },
        teams: { include: { team: true } },
        requiredSkills: { include: { skill: true } },
      },
    });
    if (!parent?.isOpportunityGroupParent) return null;
    return parent as GroupParentWithRelations;
  }

  private static async createGroupChildSlotInternal(
    parent: GroupParentWithRelations,
    parentId: string,
    slot: GroupChildSlotInput,
  ): Promise<{ success: boolean; message: string; childId?: string }> {
    const scheduleAnchor = slot.hasTime
      ? slot.hasStartTime
        ? slot.startDate
        : slot.endDate
      : null;
    const lastCallToVolunteers = scheduleAnchor
      ? dayjs(scheduleAnchor).subtract(3, "hour").toDate()
      : parent.lastCallToVolunteers;

    const child = repo(Opportunity).create({
      ...pickGroupChildBaseFields(parent),
      ...slot,
      lastCallToVolunteers,
      id: undefined,
      isOpportunityGroupParent: false,
      parentOpportunityId: parentId,
      isHidden: true,
      isClosed: false,
      isFull: false,
      isRepeated: false,
      repeatedOpportunityId: undefined,
    });

    applyRequestApprovedAtForSave(child, { isNew: true });
    const saved = await repo(Opportunity).save(child);
    if (!saved) {
      return { success: false, message: ADMIN_GROUP_CREATE_INSTANCE_ERROR };
    }

    if (!slot.isRemote) {
      await syncOpportunityCoordinatesGeog(saved.id, saved.coordinates);
    }

    const selectedTeams =
      parent.teams
        ?.map((t) => (t as { team?: Team }).team)
        .filter((team): team is Team => !!team) ?? [];
    if (selectedTeams.length > 0) {
      const teamsResponse = await Opportunity.saveTeamsForOpportunity(
        saved.id,
        selectedTeams,
      );
      if (!teamsResponse.success) {
        throw new Error(teamsResponse.message);
      }
    }

    const categories =
      parent.categories?.map((c) => (c as { category?: { id: string } }).category ?? c) ??
      [];
    const populations =
      parent.populations?.map((p) => (p as { population?: { id: string } }).population ?? p) ??
      [];

    if (categories.length > 0) {
      await Opportunity.saveCategoriesForOpportunity(
        saved.id,
        categories as Parameters<
          typeof Opportunity.saveCategoriesForOpportunity
        >[1],
      );
    }
    if (populations.length > 0) {
      await Opportunity.savePopulationsForOpportunity(
        saved.id,
        populations as Parameters<
          typeof Opportunity.savePopulationsForOpportunity
        >[1],
      );
    }

    if (parent.requiredSkills?.length) {
      const skillsResponse = await Opportunity.saveSkillsForOpportunity(
        saved.id,
        parent.requiredSkills,
      );
      if (!skillsResponse.success) {
        throw new Error(skillsResponse.message);
      }
    }

    return {
      success: true,
      message: ADMIN_GROUP_CREATE_INSTANCE_SUCCESS,
      childId: saved.id,
    };
  }

  static async syncGroupParentFromChildren(parentId: string) {
    const children = await repo(Opportunity).find({
      where: { parentOpportunityId: parentId, isDeleted: false },
      orderBy: { startDate: "asc" },
    });

    if (children.length === 0) return;

    const filledById = await OpportunityMethods.getParticipantsCountByOpportunityIds(
      children.map((c) => c.id),
    );

    const payload = computeParentSyncFromChildren(
      children.map(
        (c): OpportunityGroupChildSnapshot => ({
          startDate: c.startDate ?? null,
          endDate: c.endDate ?? null,
          lastCallToVolunteers: c.lastCallToVolunteers,
          coordinates: c.coordinates,
          isRemote: c.isRemote,
          location: c.location ?? "",
          isDeleted: c.isDeleted,
          isClosed: c.isClosed,
          isFull: c.isFull,
          status: c.status,
          participantsCount: filledById.get(c.id) ?? 0,
          amount: c.amount,
        }),
      ),
    );
    if (!payload) return;

    const notDeleted = children.filter((c) => !c.isDeleted);
    const allClosed =
      notDeleted.length > 0 && notDeleted.every((c) => c.isClosed);
    const anyOpenChild = notDeleted.some(
      (c) =>
        !c.isClosed &&
        !opportunitySeatsAreFull({
          isFull: c.isFull,
          participantsCount: filledById.get(c.id) ?? 0,
          amount: c.amount,
        }),
    );

    await repo(Opportunity).update(parentId, {
      isOpportunityGroupParent: true,
      startDate: payload.startDate,
      endDate: payload.endDate,
      lastCallToVolunteers: payload.lastCallToVolunteers,
      coordinates: payload.coordinates as Opportunity["coordinates"],
      isRemote: payload.isRemote,
      location: payload.location,
      isClosed: allClosed,
      isFull: notDeleted.length > 0 && !anyOpenChild,
    });
  }

  /** Mirrors `getOpportunitiesCount` in opportunity-queries-kysely.service.ts */
  private static async getParticipantsCountByOpportunityIds(
    opportunityIds: string[],
  ): Promise<Map<string, number>> {
    const counts = new Map<string, number>();
    if (opportunityIds.length === 0) return counts;
    for (const id of opportunityIds) counts.set(id, 0);

    const [participants, groupLinks] = await Promise.all([
      repo(OpportunityParticipant).find({
        where: {
          opportunityId: { $in: opportunityIds },
          status: "APPROVED",
        },
        include: { group: true },
      }),
      repo(GroupOpportunity).find({
        where: { opportunityId: { $in: opportunityIds } },
        include: { group: true },
      }),
    ]);

    const linkedGroups = new Set(
      groupLinks.map((go) => `${go.opportunityId}:${go.groupId}`),
    );
    const countedOrphans = new Set<string>();
    for (const p of participants) {
      if (!p.groupId || p.groupId === "") {
        counts.set(
          p.opportunityId,
          (counts.get(p.opportunityId) ?? 0) + 1,
        );
        continue;
      }
      const key = `${p.opportunityId}:${p.groupId}`;
      if (linkedGroups.has(key) || countedOrphans.has(key)) continue;
      countedOrphans.add(key);
      counts.set(
        p.opportunityId,
        (counts.get(p.opportunityId) ?? 0) + (p.group?.size ?? 1),
      );
    }
    for (const go of groupLinks) {
      counts.set(
        go.opportunityId,
        (counts.get(go.opportunityId) ?? 0) + (go.group?.size ?? 0),
      );
    }
    return counts;
  }
}
